// Liste der Opportunities (Tabelle wie die Lead-Listen: 34-px-Zeilen, dünne Linien, Fokuszeile mit j und k, Enter öffnet den
// Lead, e bearbeitet). Spaltenköpfe sortieren. Welche Spalten stehen, bestimmt die Bedienleiste (5B), hier nur die Anzeige.
import { useEffect, useRef, type ReactNode } from "react";
import { Link } from "react-router-dom";
import type { OpportunityStatus } from "../api";
import type { OpportunityZeile } from "../api/opportunities";
import { Avatar, StatusPille } from "../components/Bausteine";
import { Icon } from "../components/Icon";
import { MenueKnopf } from "../components/Menue";
import { useSpaltenZiehen } from "../components/SpaltenZiehen";
import { useTasten } from "../werkzeuge/tasten";
import { zeitpunktLang, zeitpunktText } from "../werkzeuge/zeit";
import { OPP_SPALTEN, datumKurz, euro, periodeKurz, statusFarbe, type OppSpalte } from "./logik";
import { opportunityMenue } from "./menue";

interface Props {
  zeilen: OpportunityZeile[];
  /** Schlüssel der gezeigten Spalten in Reihenfolge (`OPP_SPALTEN`) */
  spalten: string[];
  /** Sortierung als API-Wert, z. B. "-value" */
  sort: string;
  onSort: (api: string) => void;
  fokus: number;
  setFokus: (i: number) => void;
  onOeffnen: (opp: OpportunityZeile) => void;
  onBearbeiten: (opp: OpportunityZeile) => void;
  onStatus: (opp: OpportunityZeile, statusId: string) => void;
  darfAendern: (opp: OpportunityZeile) => boolean;
  statusVon: (opp: OpportunityZeile) => OpportunityStatus[];
  onEnde?: () => void;
  fuss?: ReactNode;
  /** Spaltenbreiten in px je Spaltenschlüssel (je Nutzer gespeichert) */
  breiten?: Record<string, number>;
  onBreite?: (key: string, px: number | null) => void;
  /** Spalte per Ziehen im Kopf verschoben: `von` steht danach vor `nach` */
  onVerschieben?: (von: string, nach: string) => void;
}

const ABSTEIGEND_ZUERST = new Set(["value", "confidence", "date_created", "date_updated"]);

function zelle(opp: OpportunityZeile, s: OppSpalte): ReactNode {
  switch (s.key) {
    case "lead":
      return (
        <Link to={`/lead/${encodeURIComponent(opp.lead_id)}`} className="tabelle__name opp-tabelle__lead" onClick={(e) => e.stopPropagation()} title={opp.lead_name ?? undefined}>
          {opp.lead_name ?? "Lead ohne Namen"}
        </Link>
      );
    case "value":
      return opp.value ? (
        <span title={periodeKurz(opp.value_period) ? `${euro(opp.value)} ${periodeKurz(opp.value_period)}` : undefined}>
          {euro(opp.value)}
          {periodeKurz(opp.value_period) ? <span className="leise"> {periodeKurz(opp.value_period)}</span> : null}
        </span>
      ) : (
        <span className="leise">–</span>
      );
    case "confidence":
      return opp.confidence !== null && opp.confidence !== undefined ? `${opp.confidence} %` : <span className="leise">–</span>;
    case "close_date":
      return datumKurz(opp.status_type === "lost" ? opp.date_lost : opp.date_won) || <span className="leise">–</span>;
    case "status":
      return <StatusPille label={opp.status_label} farbe={statusFarbe(opp.status_type, opp.status_color)} schlicht />;
    case "user":
      return opp.user_name ? (
        <span className="opp-tabelle__nutzer">
          <Avatar name={opp.user_name} id={opp.user_id} groesse={18} />
          <span>{opp.user_name}</span>
        </span>
      ) : (
        <span className="leise">–</span>
      );
    case "date_updated":
      return <span title={zeitpunktLang(opp.date_updated)}>{zeitpunktText(opp.date_updated)}</span>;
    case "date_created":
      return <span title={zeitpunktLang(opp.date_created)}>{zeitpunktText(opp.date_created)}</span>;
    case "contact":
      return opp.contact_name ?? <span className="leise">–</span>;
    case "lead_status":
      return opp.lead_status_label ?? <span className="leise">–</span>;
    case "note":
      return opp.note ? <span className="opp-tabelle__notiz" title={opp.note}>{opp.note.split("\n")[0]}</span> : <span className="leise">–</span>;
    case "pipeline":
      return opp.pipeline_name ?? <span className="leise">–</span>;
    default:
      return null;
  }
}

export function OpportunityTabelle({ zeilen, spalten, sort, onSort, fokus, setFokus, onOeffnen, onBearbeiten, onStatus, darfAendern, statusVon, onEnde, fuss, breiten, onBreite, onVerschieben }: Props) {
  const koerper = useRef<HTMLTableSectionElement>(null);
  const ende = useRef<HTMLDivElement>(null);
  const endeRef = useRef(onEnde);
  endeRef.current = onEnde;
  const gezeigt = spalten.map((k) => OPP_SPALTEN.find((s) => s.key === k)).filter((s): s is OppSpalte => !!s);
  const ziehen = useSpaltenZiehen({ breiten, onBreite, onVerschieben, fest: (k) => !!OPP_SPALTEN.find((s) => s.key === k)?.fest });
  const sortFeld = sort.replace(/^-/, "");
  const absteigend = sort.startsWith("-");

  useEffect(() => {
    koerper.current?.querySelector<HTMLElement>(`tr[data-index="${fokus}"]`)?.scrollIntoView({ block: "nearest" });
  }, [fokus]);

  useEffect(() => {
    const el = ende.current;
    if (!el) return;
    const beobachter = new IntersectionObserver(
      (e) => {
        if (e.some((x) => x.isIntersecting)) endeRef.current?.();
      },
      { rootMargin: "300px" },
    );
    beobachter.observe(el);
    return () => beobachter.disconnect();
  }, []);

  useTasten({
    j: () => setFokus(Math.min(fokus + 1, zeilen.length - 1)),
    ArrowDown: () => setFokus(Math.min(fokus + 1, zeilen.length - 1)),
    k: () => setFokus(Math.max(fokus - 1, 0)),
    ArrowUp: () => setFokus(Math.max(fokus - 1, 0)),
    Enter: () => {
      const z = zeilen[fokus];
      if (z) onOeffnen(z);
    },
    e: () => {
      const z = zeilen[fokus];
      if (z && darfAendern(z)) onBearbeiten(z);
    },
  });

  const sortieren = (s: OppSpalte) => {
    if (!s.sort) return;
    if (sortFeld === s.sort) onSort(absteigend ? s.sort : `-${s.sort}`);
    else onSort(ABSTEIGEND_ZUERST.has(s.sort) ? `-${s.sort}` : s.sort);
  };

  return (
    <div className="tabelle-rahmen">
      <table className="tabelle tabelle--opps">
        <thead>
          <tr>
            {gezeigt.map((s) => {
              const aktiv = !!s.sort && s.sort === sortFeld;
              return (
                <th key={s.key} {...ziehen.kopfEigenschaften(s.key, `opp-col-${s.key}${s.rechts ? " opp-rechts" : ""}`)} aria-sort={aktiv ? (absteigend ? "descending" : "ascending") : undefined}>
                  {s.sort ? (
                    <button type="button" className={`tabelle__sort${aktiv ? " tabelle__sort--an" : ""}`} onClick={() => sortieren(s)} title={`${s.label}: sortieren`}>
                      <span className="tabelle__sort-text">{s.label}</span>
                      {aktiv ? <Icon name={absteigend ? "pfeil-runter" : "pfeil-hoch"} groesse={13} /> : <Icon name="sortieren" groesse={13} className="tabelle__sort-icon" />}
                    </button>
                  ) : (
                    <span className="tabelle__kopf">{s.label}</span>
                  )}
                  {ziehen.griff(s.key, s.label)}
                </th>
              );
            })}
            <th className="opp-col-aktionen" aria-hidden="true" />
          </tr>
        </thead>
        <tbody ref={koerper}>
          {zeilen.map((z, i) => {
            const darf = darfAendern(z);
            return (
              <tr
                key={z.id}
                data-index={i}
                className={i === fokus ? "tabelle__zeile--fokus" : undefined}
                aria-selected={i === fokus}
                onClick={(e) => {
                  if ((e.target as HTMLElement).closest("button, a, input, [role='menu']")) return;
                  setFokus(i);
                  onOeffnen(z);
                }}
              >
                {gezeigt.map((s) => (
                  <td key={s.key} className={`opp-col-${s.key}${s.rechts ? " opp-rechts" : ""}`} style={ziehen.zellStil(s.key)}>
                    <div className="zelle" style={ziehen.inhaltStil(s.key)}>{zelle(z, s)}</div>
                  </td>
                ))}
                <td className="opp-col-aktionen">
                  <span className="opp-tabelle__aktionen">
                    <button
                      type="button"
                      className="symbolknopf symbolknopf--klein"
                      title={darf ? "Opportunity bearbeiten (e)" : "Fremde Opportunity: keine Berechtigung"}
                      aria-label="Opportunity bearbeiten"
                      disabled={!darf}
                      onClick={() => onBearbeiten(z)}
                    >
                      <Icon name="stift" groesse={14} />
                    </button>
                    <MenueKnopf
                      knopfKlasse="symbolknopf symbolknopf--klein"
                      titel="Mehr"
                      ausrichtung="end"
                      breite={220}
                      punkte={opportunityMenue({
                        opp: z,
                        statuses: statusVon(z),
                        darfAendern: darf,
                        onOeffnen: () => onOeffnen(z),
                        onBearbeiten: () => onBearbeiten(z),
                        onStatus: (id) => onStatus(z, id),
                      })}
                    >
                      <Icon name="mehr" groesse={14} />
                    </MenueKnopf>
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div ref={ende} className="tabelle__ende" />
      {fuss}
    </div>
  );
}
